import { stringify, parse } from 'query-string';
import { isNullOrUndefined } from '@adsk/bim360-matrix-react-components';
import {
  ISSUE_FILTER_PROPERTIES,
  SORTING_PARAMS,
  PAGE_PARAMS,
  BOOLEAN_QUERY_PARAMS,
  ARRAY_QUERY_PARAMS,
} from '../constants/issue-query-params';

export const parseQueryString = parse;

export const stringifyQuery = (query) => stringify(query, { arrayFormat: 'brackets' });

const URL_PARAMS_BLACKLIST = [PAGE_PARAMS.OFFSET];

const booleanParamsToUrl = (queryParams) => {
  const updatedParams = { ...queryParams };
  // Boolean parameters - if they're true then we'll stringify "true". Otherwise they'll be discarded from params list
  // eslint-disable-next-line no-return-assign
  BOOLEAN_QUERY_PARAMS.forEach((param) =>
    updatedParams[param]
      ? (updatedParams[param] = updatedParams[param].toString())
      : delete updatedParams[param]
  );

  return updatedParams;
};

const booleanUrlToParams = (queryParams) => {
  const updatedParams = { ...queryParams };
  BOOLEAN_QUERY_PARAMS.forEach((param) => {
    if (updatedParams[param]) {
      updatedParams[param] = updatedParams[param].toLowerCase() === 'true';
    }
  });

  return updatedParams;
};

const cleanupQueryParams = (queryParams) => {
  const updatedParams = { ...queryParams };
  URL_PARAMS_BLACKLIST.forEach((param) => delete updatedParams[param]);

  const keys = Object.keys(updatedParams);
  keys.forEach((param) => {
    if (isNullOrUndefined(updatedParams[param]) || !updatedParams[param].length) {
      delete updatedParams[param];
    }
  });

  return booleanParamsToUrl(updatedParams);
};

/*
 * Search related helpers
 */
const WILD_CARD_OPERAND = '%';
const operands = ['=', '!', '\\.\\.\\.', ',', 'null', WILD_CARD_OPERAND, '~'];

export const stripSearchQuery = (searchExpression) => {
  if (!searchExpression) {
    return '';
  }

  const lastCharIndex = searchExpression.length - 1;
  const sliceBeginIndex = searchExpression[0] === WILD_CARD_OPERAND ? 1 : 0;
  const sliceEndIndex =
    searchExpression[lastCharIndex] === WILD_CARD_OPERAND ? lastCharIndex : undefined;
  return searchExpression.slice(sliceBeginIndex, sliceEndIndex);
};

export const updateUrlWithQueryParams = (queryParams) => {
  const currentParams = parseQueryString(window.location.search);
  const updatedParams = cleanupQueryParams({ ...currentParams, ...queryParams });
  let updateResult = { hasBeenUpdated: false };

  // Stripping the wild cards so they won't be in the query param url
  if (updatedParams[ISSUE_FILTER_PROPERTIES.SEARCH]) {
    updatedParams[ISSUE_FILTER_PROPERTIES.SEARCH] = stripSearchQuery(
      updatedParams[ISSUE_FILTER_PROPERTIES.SEARCH]
    );
  }

  let queryParamsString = stringifyQuery(updatedParams);
  const { pathname } = window.location;

  // Preventing push to history when no change is needed. Comparison is valid since stringifyQuery sorts the parameters
  // We're parsing and stringifying the window.location.search since the consuming app could've changed the order of the query parameters
  if (queryParamsString !== stringifyQuery(currentParams)) {
    queryParamsString = queryParamsString.length ? `?${queryParamsString}` : '';
    window.history.pushState(
      { pathname, queryParamsString },
      '',
      `${pathname}${queryParamsString}`
    );
    updateResult = { pathname, queryParamsString, hasBeenUpdated: true };
  }

  return updateResult;
};

const createRegexFromOperandsArray = (operandsArray) => {
  const concatOperands = operandsArray.join('|');
  return new RegExp(concatOperands, 'gi');
};

const operandsRegex = createRegexFromOperandsArray(operands);

const basicQueryTransformer = (value, operandToIgnore) => {
  if (value === null) {
    return '\\null';
  }
  if (typeof value !== 'string') {
    return value;
  }
  const currentOperandRegex = operandToIgnore
    ? createRegexFromOperandsArray(operands.filter((operand) => operand !== operandToIgnore))
    : operandsRegex;

  return value.replace(currentOperandRegex, (matched) => {
    return `\\${matched}`;
  });
};

export const createSearchQuery = (searchExpression) => {
  return `${WILD_CARD_OPERAND}${basicQueryTransformer(searchExpression)}${WILD_CARD_OPERAND}`;
};

/**
 * Returns rfis-ui relevant queryParams object.
 * If no relevant params found - returns empty object
 */
export const getIssuesUIQueryParamsFromUrl = (urlParams = window.location.search) => {
  const relevantParams = {};

  if (urlParams.length) {
    const params = parseQueryString(urlParams);
    const keysToCheck = [].concat(
      Object.values(ISSUE_FILTER_PROPERTIES),
      Object.values(SORTING_PARAMS)
    );

    keysToCheck.forEach((key) => {
      const value = params[key];

      if (value) {
        const shouldConvertToArr = ARRAY_QUERY_PARAMS.includes(key) && typeof value === 'string';
        relevantParams[key] = shouldConvertToArr ? [value] : value;
      }
    });
  }

  // Stripping the wild cards so they won't be in the query param url
  if (relevantParams[ISSUE_FILTER_PROPERTIES.SEARCH]) {
    relevantParams[ISSUE_FILTER_PROPERTIES.SEARCH] = createSearchQuery(
      relevantParams[ISSUE_FILTER_PROPERTIES.SEARCH]
    );
  }

  return booleanUrlToParams(relevantParams);
};

export const getLocationObjectFromUrl = () => {
  const { pathname } = window.location;
  const queryParamsString = stringifyQuery(getIssuesUIQueryParamsFromUrl());
  return { pathname, queryParamsString };
};

/**
 * Determines if the rfis-ui query params have changed.
 * @param {object} comparedLocation - the location to compare with. Contains pathname and queryParamsString
 * @returns {boolean}
 */
export const hasIssueUIParamsChanged = (previousLocation, newLocation) => {
  // If previousLocation is empty it means that we've just loaded rfis-ui and we should return true
  if (!previousLocation) {
    return true;
  }
  // If pathname is different than there has been a routing change and the app/package should handle in the components level
  if (newLocation.pathname !== previousLocation.pathname) {
    return false;
  }

  const newIssuesUIParams = getIssuesUIQueryParamsFromUrl(newLocation.queryParamsString);
  const lastIssuesUIParams = getIssuesUIQueryParamsFromUrl(previousLocation.queryParamsString);
  return stringifyQuery(newIssuesUIParams) !== stringifyQuery(lastIssuesUIParams);
};
